import { useState } from "react";
import classNames from "classnames";
import { useAcl, useAddAcl, useRemoveAcl } from "../../hooks/useTasks";
import { useUsers, userLabel } from "../../hooks/useDirectory";
import { ACL_LEVELS, type AclLevel } from "../../lib/design";
import { UserPicker } from "../ui/Typeahead";

const LEVEL_LABEL: Record<AclLevel, string> = {
  read: "Lecture",
  comment: "Commentaire",
  write: "Écriture",
  admin: "Admin",
};

/** Partage explicite d'une tâche (task_acl_entries · /{id}/acl). Complète la
 * visibilité private/team/tenant par des accès nominatifs. */
export function AclSection({ taskId }: { taskId: string }) {
  const acl = useAcl(taskId);
  const users = useUsers();
  const add = useAddAcl();
  const remove = useRemoveAcl();
  const [level, setLevel] = useState<AclLevel>("read");

  return (
    <div className="detail-section">
      <div className="detail-section-title">Partage · ACL</div>
      {(acl.data ?? []).map((e) => (
        <div key={e.id} className="prop-row" style={{ alignItems: "center" }}>
          <span className="prop-value">
            {userLabel(users.data, e.subject_ref)}
            {e.subject_type !== "user" && (
              <span className="tag" style={{ marginLeft: 6 }}>
                {e.subject_type}
              </span>
            )}
          </span>
          <span
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span className="tag">{LEVEL_LABEL[e.access_level]}</span>
            <button
              className="icon-btn"
              onClick={() => remove.mutate({ taskId, aclId: e.id })}
              aria-label="Retirer l'accès"
              title="Retirer"
            >
              ×
            </button>
          </span>
        </div>
      ))}
      {(acl.data ?? []).length === 0 && (
        <div style={{ fontSize: 11.5, color: "var(--ink-ghost)" }}>
          Aucun partage nominatif.
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <div
          className="chip-picker"
          role="radiogroup"
          aria-label="Niveau d'accès"
        >
          {ACL_LEVELS.map((l) => (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={level === l}
              className={classNames("chip", "chip-choice", {
                active: level === l,
              })}
              style={
                level === l
                  ? { color: "var(--accent)", background: "var(--accent-wash)" }
                  : undefined
              }
              onClick={() => setLevel(l)}
            >
              {LEVEL_LABEL[l]}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6 }}>
          <UserPicker
            placeholder="Partager avec…"
            exclude={(acl.data ?? []).map((e) => e.subject_ref)}
            onPick={(v) =>
              add.mutate({
                taskId,
                body: {
                  subject_type: "user",
                  subject_ref: v.ref,
                  access_level: level,
                },
              })
            }
          />
        </div>
      </div>
    </div>
  );
}
